import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { api, fmt } from "@/lib/game";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Header } from "@/components/game/Header";
import { Landing } from "@/components/game/Landing";
import { GameBoard } from "@/components/game/GameBoard";
import { Summary } from "@/components/game/Summary";
import { LearnMoreModal } from "@/components/game/LearnMoreModal";
import { AuthModal } from "@/components/game/AuthModal";
import { MyGames } from "@/components/game/MyGames";
import { Facilitator } from "@/components/game/Facilitator";
import { ShareView } from "@/components/game/ShareView";
import { Questionnaire } from "@/components/game/Questionnaire";
import { ProfileSetup } from "@/components/game/ProfileSetup";
import { MyProgress } from "@/components/game/MyProgress";
import { BadgeUnlockModal } from "@/components/game/BadgeUnlockModal";
import { MyCommitments } from "@/components/game/MyCommitments";
import MyProfile from "@/components/game/MyProfile";
import { AdminDashboard } from "@/components/game/AdminDashboard";
import { PlayTogether } from "@/components/game/PlayTogether";
import { HouseholdShareView } from "@/components/game/HouseholdShareView";

const STORAGE_KEY = "burgeon_session_id";

function Main() {
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [session, setSession] = useState(null);
  const [screen, setScreen] = useState("landing"); // landing | game | summary | mygames | facilitator | share
  const [starting, setStarting] = useState(false);
  const [submittingQ, setSubmittingQ] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [shareId, setShareId] = useState(null);
  const [shareHousehold, setShareHousehold] = useState(null);
  const [resumable, setResumable] = useState(null);

  const [decisions, setDecisions] = useState([]);
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [pendingBadges, setPendingBadges] = useState([]);
  const activityRef = useRef(null);

  const totalRounds = settings?.total_rounds || 8;

  const loadReview = useCallback(async (sessionId) => {
    setReviewLoading(true);
    try {
      const detail = await api.get(`/sessions/${sessionId}`);
      setDecisions(detail.data.decisions || []);
      const { data } = await api.post(`/sessions/${sessionId}/review`);
      setReview(data);
      if (data.newly_earned_badges?.length) setPendingBadges((b) => [...b, ...data.newly_earned_badges]);
    } catch (e) {
      /* review endpoint has its own fallback */
    } finally {
      setReviewLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (e) {
        /* noop */
      }

      const params = new URLSearchParams(window.location.search);
      const share = params.get("share");
      if (share) {
        setShareId(share);
        setScreen("share");
        return;
      }

      const household = params.get("household");
      if (household) {
        setShareHousehold(household);
        setScreen("householdshare");
        return;
      }

      const reset = params.get("reset");
      if (reset) {
        setResetToken(reset);
        setAuthOpen(true);
        window.history.replaceState(null, "", window.location.pathname);
      }

      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const { data } = await api.get(`/sessions/${stored}`);
          setSession(data.session);
          setDecisions(data.decisions || []);
          if (data.session.status === "complete") {
            setScreen("summary");
            if (data.review) setReview(data.review);
            else loadReview(stored);
          } else {
            setScreen("game");
          }
        } catch (e) {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    })();
  }, [loadReview]);

  // Cross-device sync: surface a signed-in user's in-progress game to resume anywhere
  useEffect(() => {
    if (user && typeof user === "object") {
      api.get("/me/sessions")
        .then(({ data }) => setResumable((data || []).find((s) => s.status === "in_progress") || null))
        .catch(() => setResumable(null));
    } else {
      setResumable(null);
    }
  }, [user]);

  // Commitment check-in nudge: when a signed-in user returns and has due check-ins
  const nudgedRef = useRef(false);
  useEffect(() => {
    if (user && typeof user === "object" && !nudgedRef.current) {
      api.get("/me/commitments/due").then(({ data }) => {
        const due = data?.due || [];
        if (due.length) {
          nudgedRef.current = true;
          toast("Time to check in on your commitment 🌱", {
            description: due[0].text,
            action: { label: "Review", onClick: () => setScreen("commitments") },
            duration: 8000,
          });
        }
      }).catch(() => {});
    }
  }, [user]);

  // Daily streak: record a meaningful action once when a logged-in user enters a game
  useEffect(() => {
    if (screen === "game" && session?.id && user && typeof user === "object" && activityRef.current !== session.id) {
      activityRef.current = session.id;
      api.post("/me/activity").then(({ data }) => {
        if (data.reset) {
          toast("Welcome back! Your streak restarted at day 1 — every fresh start counts. 🌱");
        } else if (data.current_streak > 1) {
          toast.success(`${data.current_streak}-day streak! Keep it growing.`);
        }
        if (data.milestone) toast.success(`🎉 ${data.milestone}-day streak milestone unlocked!`);
        if (data.newly_earned_badges?.length) setPendingBadges((b) => [...b, ...data.newly_earned_badges]);
        refreshUser();
      }).catch(() => {});
    }
  }, [screen, session, user, refreshUser]);

  const handleStart = async (nickname, country, joinCode) => {
    setStarting(true);
    try {
      const { data } = await api.post("/sessions", {
        nickname,
        country,
        group_code: joinCode || null,
      });
      const last = (data.salary_events || []).slice(-1)[0];
      if (last?.paid) toast.success(`Monthly salary paid: +${fmt(last.amount)} added to your wallet`);
      else if (last) toast(`No salary for ${last.month} — you played ${last.days_played} of ${last.required} days needed. Play twice a week to earn it.`);
      localStorage.setItem(STORAGE_KEY, data.id);
      setSession(data);
      setReview(null);
      setDecisions([]);
      setScreen(typeof user === "object" && user ? "game" : "profile");
    } catch (e) {
      const msg = e.response?.data?.detail || "Couldn't start the game";
      alert(typeof msg === "string" ? msg : "Couldn't start the game");
    } finally {
      setStarting(false);
    }
  };

  const handleProfileComplete = async (profile) => {
    setSubmittingQ(true);
    try {
      if (profile && session?.id) {
        await api.post(`/sessions/${session.id}/profile`, profile);
        setSession((s) => ({ ...s, ...profile }));
      }
    } catch (e) {
      /* non-blocking */
    } finally {
      setSubmittingQ(false);
      setScreen("questionnaire");
    }
  };

  const handleQuestionnaireComplete = async (responses) => {
    setSubmittingQ(true);
    try {
      if (session?.id && responses && (responses.length || (typeof user === "object" && user))) {
        const { data } = await api.post(`/sessions/${session.id}/questionnaire`, { responses });
        setSession((s) => ({ ...s, ...(data.session || {}), questionnaire: responses }));
      }
    } catch (e) {
      /* non-blocking — proceed into the game regardless */
    } finally {
      setSubmittingQ(false);
      setScreen("game");
      refreshUser();
    }
  };

  // One-time onboarding right after a player's first sign-in
  const [onboardProfile, setOnboardProfile] = useState(null);
  const onboardShownRef = useRef(false);
  useEffect(() => {
    if (user && typeof user === "object" && user.needs_onboarding && !onboardShownRef.current) {
      onboardShownRef.current = true;
      setScreen("onboard-profile");
    }
  }, [user]);

  const finishOnboarding = async (responses) => {
    setSubmittingQ(true);
    try {
      await api.post("/me/onboarding", { ...(onboardProfile || {}), responses: responses || [] });
      await refreshUser();
      toast.success("All set — your answers will shape every game you play.");
    } catch (e) {
      toast.error("Couldn't save your answers — you can carry on playing.");
    } finally {
      setSubmittingQ(false);
      setScreen("landing");
    }
  };

  const handleComplete = (finalSession) => {
    setSession(finalSession);
    setScreen("summary");
    loadReview(finalSession.id);
  };

  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
    setReview(null);
    setDecisions([]);
    setScreen("landing");
  };

  const goHome = () => {
    if (shareId || shareHousehold) {
      window.history.replaceState(null, "", window.location.pathname);
      setShareId(null);
      setShareHousehold(null);
    }
    setScreen("landing");
  };

  const openSavedGame = async (g) => {
    try {
      const { data } = await api.get(`/sessions/${g.id}`);
      setSession(data.session);
      setDecisions(data.decisions || []);
      localStorage.setItem(STORAGE_KEY, data.session.id);
      if (data.session.status === "complete") {
        setReview(data.review || null);
        setScreen("summary");
        if (!data.review) loadReview(data.session.id);
      } else {
        setScreen("game");
      }
    } catch (e) {
      /* noop */
    }
  };

  return (
    <div className="App min-h-screen bg-[#0B0F17] text-slate-100">
      {screen !== "share" && screen !== "householdshare" && (
        <Header
          session={screen === "game" ? session : null}
          totalRounds={totalRounds}
          onLearnMore={() => setLearnOpen(true)}
          onReset={session && screen === "game" ? handleReset : null}
          onHome={goHome}
          onMyGames={() => setScreen("mygames")}
          onFacilitator={() => setScreen("facilitator")}
          onMyProgress={() => setScreen("progress")}
          onMyCommitments={() => setScreen("commitments")}
          onMyProfile={() => setScreen("myprofile")}
          onAdmin={() => setScreen("admin")}
          onPlayTogether={() => setScreen("playtogether")}
          onSignIn={() => setAuthOpen(true)}
        />
      )}

      {screen === "landing" && (
        <Landing
          settings={settings}
          user={typeof user === "object" ? user : null}
          onSignIn={() => setAuthOpen(true)}
          onStart={handleStart}
          starting={starting}
          resume={resumable}
          onResume={() => resumable && openSavedGame({ id: resumable.id })}
          onPlayTogether={() => setScreen("playtogether")}
        />
      )}

      {screen === "profile" && session && (
        <ProfileSetup
          initial={typeof user === "object" ? user : null}
          submitting={submittingQ}
          onComplete={handleProfileComplete}
        />
      )}

      {screen === "onboard-profile" && typeof user === "object" && user && (
        <ProfileSetup
          initial={user}
          submitting={submittingQ}
          onComplete={(p) => {
            setOnboardProfile(p);
            setScreen("onboard-questionnaire");
          }}
        />
      )}

      {screen === "onboard-questionnaire" && typeof user === "object" && user && (
        <Questionnaire nickname={user.name || "there"} submitting={submittingQ} onComplete={finishOnboarding} />
      )}

      {screen === "questionnaire" && session && (
        <Questionnaire
          nickname={session.nickname}
          submitting={submittingQ}
          onComplete={handleQuestionnaireComplete}
        />
      )}

      {screen === "game" && session && (
        <GameBoard
          session={session}
          totalRounds={totalRounds}
          onUpdate={setSession}
          onComplete={handleComplete}
        />
      )}

      {screen === "summary" && session && (
        <Summary
          session={session}
          decisions={decisions}
          review={review}
          reviewLoading={reviewLoading}
          onRestart={handleReset}
          onSignIn={() => setAuthOpen(true)}
        />
      )}

      {screen === "mygames" && (
        <MyGames onOpen={openSavedGame} onNew={goHome} />
      )}

      {screen === "facilitator" && <Facilitator />}

      {screen === "progress" && <MyProgress onSignIn={() => setAuthOpen(true)} />}

      {screen === "commitments" && (
        <MyCommitments onSignIn={() => setAuthOpen(true)} onNew={goHome} />
      )}

      {screen === "admin" && <AdminDashboard onSignIn={() => setAuthOpen(true)} />}

      {screen === "myprofile" && typeof user === "object" && user && (
        <MyProfile
          onBack={goHome}
          onSaved={async (ids) => {
            refreshUser();
            if (session && ids.includes(session.id)) {
              const { data } = await api.get(`/sessions/${session.id}`);
              setSession(data.session);
            }
          }}
        />
      )}

      {screen === "playtogether" && <PlayTogether onExit={goHome} onSignIn={() => setAuthOpen(true)} />}

      {screen === "share" && shareId && (
        <ShareView sessionId={shareId} onPlay={goHome} />
      )}

      {screen === "householdshare" && shareHousehold && (
        <HouseholdShareView code={shareHousehold} onPlay={goHome} />
      )}

      <LearnMoreModal open={learnOpen} onOpenChange={setLearnOpen} />
      <AuthModal
        open={authOpen}
        onOpenChange={(v) => { setAuthOpen(v); if (!v) setResetToken(""); }}
        initialToken={resetToken}
      />
      <Toaster position="top-center" richColors theme="dark" />
      <BadgeUnlockModal
        badges={pendingBadges}
        open={pendingBadges.length > 0}
        onClose={() => setPendingBadges([])}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Main />
    </AuthProvider>
  );
}

export default App;
